import React from "react";
import { Link } from "react-router-dom";
import { Accessibility, Globe2, Users, BookOpen, HeartHandshake, Sparkles, Route, Briefcase, GraduationCap, RefreshCw, Brain, Stamp } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const ulisPoints = [
  { icon: Users, t: "Un accompagnement individualisé", d: "Une coordinatrice ULIS dédiée et une équipe pluridisciplinaire au plus près des élèves." },
  { icon: BookOpen, t: "Inclusion en classe ordinaire", d: "Les élèves bénéficient des cours en classe ordinaire avec des adaptations pédagogiques personnalisées." },
  { icon: HeartHandshake, t: "Projet personnalisé", d: "Un Projet Personnalisé de Scolarisation (PPS) co-construit avec la famille et les partenaires." },
  { icon: Sparkles, t: "Insertion professionnelle", d: "Préparation au monde du travail avec stages adaptés et accompagnement vers les ESAT ou le milieu ordinaire." },
];

const upe2aPoints = [
  { icon: Globe2, t: "Apprentissage du français", d: "Cours intensifs de Français Langue Seconde (FLS) adaptés au niveau de chaque élève." },
  { icon: Users, t: "Suivi personnalisé", d: "Un enseignant référent UPE2A accompagne chaque jeune dans son parcours d'intégration." },
  { icon: BookOpen, t: "Inclusion progressive", d: "Intégration progressive dans les classes ordinaires selon les compétences acquises." },
  { icon: HeartHandshake, t: "Accueil bienveillant", d: "Médiation interculturelle, lien avec les familles et soutien dans les démarches administratives." },
];

const ParcoursCard = ({ icon: Icon, badge, titre, intro, axes, testid }) => (
  <Card data-testid={testid} className="flex flex-col">
    <div className="flex items-center gap-3">
      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate text-brand-cream">
        <Icon className="h-5 w-5" />
      </span>
      <span className="rounded-full bg-brand-sand/30 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-slate-dark">
        {badge}
      </span>
    </div>
    <h3 className="mt-5 font-heading text-xl font-bold text-brand-slate-dark">{titre}</h3>
    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{intro}</p>
    {axes && axes.length > 0 && (
      <ul className="mt-5 space-y-3 text-sm">
        {axes.map((a) => (
          <li key={a} className="flex gap-3 text-brand-slate-dark">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />
            <span className="text-muted-foreground">{a}</span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

export default function Inclusion() {
  return (
    <div data-testid="inclusion-page">
      <PageHero
        eyebrow="Inclusion · ULIS & UPE2A"
        title="Une école ouverte à tous les profils"
        subtitle="Fidèle à l'héritage de Joseph Wresinski, notre lycée accueille chaque jeune dans sa singularité. Nous portons deux dispositifs nationaux pour garantir une scolarisation adaptée à tous : ULIS et UPE2A."
        image={IMAGES.values}
      />

      {/* ULIS */}
      <Section eyebrow="Dispositif ULIS" title="Unité Localisée pour l'Inclusion Scolaire">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <div className="inline-flex items-center gap-3 rounded-full bg-brand-sage/15 px-4 py-2 text-sm font-semibold text-brand-sage-dark">
              <Accessibility className="h-4 w-4" />
              Inclusion scolaire
            </div>
            <p>
              Le dispositif ULIS du lycée professionnel Joseph Wresinski accueille des jeunes
              <strong className="text-brand-slate-dark"> présentant des troubles des fonctions cognitives</strong>.
            </p>
            <p>
              Notre coordinatrice ULIS travaille en étroite collaboration avec les enseignants, les
              familles, les AESH (Accompagnants des Élèves en Situation de Handicap) et les partenaires
              médico-sociaux.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {ulisPoints.map((p, i) => (
              <Card key={p.t} data-testid={`ulis-${i}`} className="bg-brand-sand-wrapper/40">
                <p.icon className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-4 font-heading text-base font-semibold text-brand-slate-dark">
                  {p.t}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* ULIS — 2 parcours détaillés */}
        <div className="mt-12">
          <h3 className="font-heading text-2xl font-bold text-brand-slate-dark">
            Deux parcours d'accompagnement possibles
          </h3>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
            Le dispositif ULIS propose deux modalités d'accompagnement adaptées au parcours et au
            projet de chaque jeune.
          </p>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <ParcoursCard
              icon={RefreshCw}
              badge="Parcours 1"
              titre="Jeune inscrit en CAP"
              intro="Un soutien ponctuel à la scolarisation en CAP, ajustable tout au long de l'année selon les besoins de l'élève."
              testid="ulis-parcours-cap"
            />
            <ParcoursCard
              icon={Route}
              badge="Parcours 2 — ULIS SAS"
              titre="Année de détermination"
              intro="Pour un jeune inscrit en ULIS SAS, une année qui s'articule autour de trois axes :"
              axes={[
                "Inclusion scolaire personnalisée (exploration d'un ou plusieurs CAP) pour répondre aux besoins éducatifs particuliers et au projet professionnel de chacun",
                "Acquisition des compétences sociales et civiques nécessaires à la construction de sa vie future d'adulte responsable",
                "Construction du projet professionnel de l'élève par la réalisation de stages en entreprises",
              ]}
              testid="ulis-parcours-sas"
            />
          </div>

          <div className="mt-8 flex items-start gap-4 rounded-2xl border border-brand-sage/30 bg-brand-sage/10 p-5 text-sm">
            <Stamp className="mt-0.5 h-5 w-5 shrink-0 text-brand-sage-dark" />
            <p className="text-brand-slate-dark">
              <strong>Affectation :</strong> ces deux accompagnements résultent de l'affectation par
              l'inspection académique après une décision prise en{" "}
              <strong>ESS (Équipe de Suivi de la Scolarisation)</strong>.
            </p>
          </div>
        </div>
      </Section>

      {/* UPE2A */}
      <Section
        wrapper="bg-brand-sand-wrapper/40"
        eyebrow="Dispositif UPE2A"
        title="Unité Pédagogique pour Élèves Allophones Arrivants"
      >
        <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr]">
          <div className="grid gap-5 sm:grid-cols-2">
            {upe2aPoints.map((p, i) => (
              <Card key={p.t} data-testid={`upe2a-${i}`}>
                <p.icon className="h-7 w-7 text-brand-slate-dark" />
                <h3 className="mt-4 font-heading text-base font-semibold text-brand-slate-dark">
                  {p.t}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
              </Card>
            ))}
          </div>

          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <div className="inline-flex items-center gap-3 rounded-full bg-brand-sand/30 px-4 py-2 text-sm font-semibold text-brand-slate-dark">
              <Globe2 className="h-4 w-4" />
              Élèves allophones
            </div>
            <p>
              Le dispositif UPE2A accueille des élèves nouvellement arrivés en France et ne maîtrisant
              pas encore la langue française.
            </p>
            <p>
              Notre lycée propose un accompagnement linguistique intensif couplé à une inclusion
              progressive dans les classes professionnelles ordinaires.
            </p>
          </div>
        </div>

        {/* UPE2A — 2 parcours détaillés */}
        <div className="mt-12">
          <h3 className="font-heading text-2xl font-bold text-brand-slate-dark">
            Deux parcours selon le niveau de français et le parcours antérieur
          </h3>
          <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
            En fonction du parcours antérieur de l'élève et de son niveau d'acquisition de la langue
            française, deux modalités d'accompagnement peuvent être proposées.
          </p>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <ParcoursCard
              icon={Brain}
              badge="Parcours 1 — UPE2A"
              titre="Année passerelle"
              intro="Pour un jeune inscrit en UPE2A, une année passerelle articulée autour des axes suivants :"
              axes={[
                "Enseignement intensif de français (lecture, écriture, expression écrite et orale) ainsi que mathématiques, sciences physiques et histoire-géographie notamment",
                "Acquisition des compétences sociales et civiques nécessaires à la construction de sa future orientation professionnelle",
                "Construction du projet professionnel par la réalisation de stages en entreprises et des inclusions dans différents CAP",
              ]}
              testid="upe2a-parcours-passerelle"
            />
            <ParcoursCard
              icon={GraduationCap}
              badge="Parcours 2"
              titre="CAP avec soutien UPE2A"
              intro="Pour un jeune inscrit en CAP avec soutien UPE2A : un soutien individualisé à la scolarisation en CAP, ajustable tout au long de l'année grâce à un retour ponctuel dans l'unité."
              testid="upe2a-parcours-cap"
            />
          </div>

          <div className="mt-8 flex items-start gap-4 rounded-2xl border border-brand-sand/40 bg-brand-cream p-5 text-sm">
            <Briefcase className="mt-0.5 h-5 w-5 shrink-0 text-brand-sand-dark" />
            <p className="text-brand-slate-dark">
              <strong>Affectation :</strong> ces deux accompagnements résultent d'une décision prise
              par l'établissement lors d'un <strong>rendez-vous d'inscription</strong> en
              collaboration avec le jeune et son référent.
            </p>
          </div>
        </div>
      </Section>

      <Section>
        <div className="rounded-3xl bg-brand-slate-dark p-10 text-brand-cream lg:p-16">
          <div className="grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="font-heading text-3xl font-bold sm:text-4xl">
                Vous souhaitez nous confier votre enfant ?
              </h2>
              <p className="mt-4 max-w-xl text-brand-cream/85">
                Nos équipes ULIS et UPE2A sont à votre disposition pour échanger sur le parcours de
                votre enfant et étudier ensemble la solution la plus adaptée.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/contact"
                data-testid="inclusion-cta-contact"
                className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
              >
                Prendre contact
              </Link>
              <Link
                to="/accompagnement"
                data-testid="inclusion-cta-accompagnement"
                className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
              >
                Notre accompagnement
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
